import { ReactNode } from "react";
import { DataId, DataSource, Filterable, Indexable, Paged } from "capitalrx-components-2.0/lib/general";
import { AccessibilityProps } from "../../AccessibilityProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { CustomReactSelectProps } from "../Dropdown/CustomReactSelectProps";
import { Placeholder } from "../Dropdown/Placeholder";
import { InputBaseProps } from "../InputBaseProps";
import { Field } from "./Field";
import { Input } from "./Input";
import { OptionLabelProvider } from "./OptionLabelProvider";

/**
 * The type of data source accepted by the `DataDropdown` component.
 */
export interface DataDropdownDataSource<TRow extends object> extends
  DataSource<TRow>,
  Paged,
  Filterable,
  Indexable<TRow> { }

/**
 * The `DataDropdown` component properties.
 */
export interface DataDropdownProps<TRow extends object>
  extends InputBaseProps<DataId | null>,
    Pick<AccessibilityProps, "aria-label"> {
  /**
   * The data source to use to retrieve dropdown items.
   */
  readonly dataSource: DataDropdownDataSource<TRow>;

  /**
   * The function to call returning the label for a particular row.
   */
  readonly optionLabelProvider: OptionLabelProvider<TRow>;

  /**
   * The field to search against. If remote, corresponds to a field alias. If not, corresponds to a dot path to the field on the row.
   */
  readonly searchField?: string;

  /**
   * Props for search input
   */
  readonly searchInputProps?: CustomReactSelectProps["searchInputProps"];

  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Indicates whether the dropdown is able to be cleared. If not set, defaults to `false`.
   */
  readonly clearable?: boolean;

  /**
   * Message to display when no options are returned from search.
   */
  readonly noOptionsMessage?: ReactNode;
}

/**
 * The `DataDropdown` component sub components.
 */
export interface DataDropdownSubComponents {
  /**
   * A React Hook Form compatible DataDropdown component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Dropdown which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

export const DataDropdown = withSubComponents(
  {
    Field,
    Placeholder,
  } as DataDropdownSubComponents,
  <TRow extends object>(props: DataDropdownProps<TRow>) => {
    return <Input {...props} optionLabelProvider={props.optionLabelProvider as OptionLabelProvider<object>} />;
  },
);
